<template>
  <el-dialog
    :visible.sync="innerVisible"
    :close-on-click-modal="false"
    width="1080px"
    append-to-body
    custom-class="picker-dialog"
  >
    <div slot="title" class="picker-title">
      选图 — {{ batch }}
      <span class="picker-sub">默认全选；取消勾选废帧，Shift+点击连选区间，确认后只导入保留的图片</span>
    </div>

    <div class="picker-toolbar">
      <el-radio-group v-model="camera" size="mini" @change="switchCamera">
        <el-radio-button label="">全部</el-radio-button>
        <el-radio-button v-for="c in cameras" :key="c.name" :label="c.name">
          {{ cameraLabel(c.name) }} {{ c.count }}
        </el-radio-button>
      </el-radio-group>
      <div class="picker-actions">
        <el-button size="mini" @click="selectAll">全选</el-button>
        <el-button size="mini" :loading="idsLoading" @click="excludeFiltered">排除当前筛选</el-button>
        <el-button size="mini" :loading="idsLoading" @click="includeFiltered">恢复当前筛选</el-button>
        <el-button size="mini" :loading="idsLoading" @click="deselectAll">全部取消</el-button>
      </div>
    </div>

    <div class="picker-summary">
      已选 <b>{{ selectedCount }}</b> / {{ pendingTotal }} 张
      <span v-if="excludedCount > 0" class="picker-muted">（已排除 {{ excludedCount }} 张）</span>
      <span class="picker-muted">当前筛选 {{ total }} 张</span>
    </div>

    <div v-loading="loading" class="picker-grid">
      <div
        v-for="row in rows"
        :key="row.id"
        class="pick-card"
        :class="{ off: isExcluded(row) }"
        @click="toggle(row, $event)"
      >
        <img :src="thumbUrl(row)" loading="lazy" class="pick-img" :alt="row.image_key">
        <el-checkbox
          class="pick-check"
          :value="!isExcluded(row)"
          @click.native.stop="toggle(row, $event)"
        />
        <div class="pick-meta">
          <span>{{ cameraLabel(cameraOf(row.image_key)) }}</span>
          <span>{{ formatTime(keyTs(row.image_key)) }}</span>
        </div>
      </div>
      <div v-if="!loading && rows.length === 0" class="picker-empty">没有待选图片</div>
    </div>

    <el-pagination
      class="pager"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="pageSize"
      :current-page="page"
      @current-change="changePage"
    />

    <div slot="footer">
      <el-button size="small" @click="close">取消</el-button>
      <el-button
        type="primary"
        size="small"
        :loading="submitting"
        :disabled="selectedCount === 0"
        @click="confirm"
      >确认并导入（保留 {{ selectedCount }} 张）</el-button>
    </div>
  </el-dialog>
</template>

<script>
import dayjs from 'dayjs';
import { fetchDatasetSelection, confirmDatasetSelection } from '../api/dataset';

const CAMERA_LABELS = { front: '前视', rear: '后视', left: '左视', right: '右视' };

export default {
  name: 'DatasetImagePicker',

  props: {
    visible: { type: Boolean, default: false },
    jobId: { type: String, default: '' },
    batch: { type: String, default: '' },
  },

  data() {
    return {
      rows: [],
      total: 0,
      pendingTotal: 0,
      cameras: [],
      camera: '',
      page: 1,
      pageSize: 120,
      loading: false,
      idsLoading: false,
      submitting: false,
      excluded: Object.create(null),
      anchorId: '',
    };
  },

  computed: {
    innerVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit('update:visible', value);
      },
    },

    excludedCount() {
      return Object.keys(this.excluded).length;
    },

    selectedCount() {
      return Math.max(0, this.pendingTotal - this.excludedCount);
    },
  },

  watch: {
    visible(on) {
      if (on) this.resetAndLoad();
    },
  },

  methods: {
    resetAndLoad() {
      this.rows = [];
      this.total = 0;
      this.pendingTotal = 0;
      this.cameras = [];
      this.camera = '';
      this.page = 1;
      this.excluded = Object.create(null);
      this.anchorId = '';
      this.load();
    },

    close() {
      this.innerVisible = false;
    },

    async load() {
      if (!this.jobId) return;
      this.loading = true;
      try {
        const res = await fetchDatasetSelection(this.jobId, {
          camera: this.camera,
          page: this.page,
          page_size: this.pageSize,
        });
        const data = res.data || {};
        this.rows = data.rows || [];
        this.total = Number(data.total) || 0;
        this.pendingTotal = Number(data.pending_total) || 0;
        this.cameras = data.cameras || [];
      } catch {
        // 错误已由拦截器统一提示
      } finally {
        this.loading = false;
      }
    },

    changePage(page) {
      this.page = page;
      this.load();
    },

    switchCamera() {
      this.page = 1;
      this.load();
    },

    isExcluded(row) {
      return !!this.excluded[row.id];
    },

    /**
     * 点击卡片或勾选框：普通点击切换当前图并记为锚点；
     * Shift+点击按当前页顺序把锚点到当前图之间一起切换成相同状态。
     */
    toggle(row, event) {
      const selected = this.isExcluded(row);
      const from = this.rows.findIndex(r => String(r.id) === String(this.anchorId));
      const to = this.rows.findIndex(r => String(r.id) === String(row.id));
      if (event && event.shiftKey && from >= 0 && to >= 0 && from !== to) {
        const start = Math.min(from, to);
        const end = Math.max(from, to);
        for (let i = start; i <= end; i++) this.setSelected(this.rows[i], selected);
        return;
      }
      this.anchorId = row.id;
      this.setSelected(row, selected);
    },

    setSelected(row, selected) {
      if (selected) this.$delete(this.excluded, row.id);
      else this.$set(this.excluded, row.id, true);
    },

    selectAll() {
      this.excluded = Object.create(null);
    },

    async fetchIds() {
      const res = await fetchDatasetSelection(this.jobId, { camera: this.camera, ids_only: 1 });
      return ((res.data || {}).ids || []);
    },

    async excludeFiltered() {
      this.idsLoading = true;
      try {
        const ids = await this.fetchIds();
        ids.forEach(id => this.$set(this.excluded, id, true));
      } catch {
        // 错误已由拦截器统一提示
      } finally {
        this.idsLoading = false;
      }
    },

    async includeFiltered() {
      this.idsLoading = true;
      try {
        const ids = await this.fetchIds();
        ids.forEach(id => this.$delete(this.excluded, id));
      } catch {
        // 错误已由拦截器统一提示
      } finally {
        this.idsLoading = false;
      }
    },

    async deselectAll() {
      this.idsLoading = true;
      try {
        const ids = await this.fetchIds();
        ids.forEach(id => this.$set(this.excluded, id, true));
      } catch {
        // 错误已由拦截器统一提示
      } finally {
        this.idsLoading = false;
      }
    },

    async confirm() {
      if (this.selectedCount === 0) {
        this.$message.warning('至少保留一张图片');
        return;
      }
      this.submitting = true;
      try {
        const res = await confirmDatasetSelection(this.jobId, Object.keys(this.excluded));
        const data = res.data || {};
        // 差集与导入都在后台跑：这里只给一句“已提交”，成功与否由进度/列表状态体现
        this.$message.success('已提交导入');
        this.innerVisible = false;
        this.$emit('confirmed', data);
      } catch {
        // 错误已由拦截器统一提示
      } finally {
        this.submitting = false;
      }
    },

    thumbUrl(row) {
      return `/api/dataset/images/${row.id}/thumb?w=240`;
    },

    cameraOf(key) {
      const match = String(key || '').match(/\/([^/]+)_\d{13}\.jpg$/);
      return match ? match[1] : '';
    },

    cameraLabel(name) {
      return CAMERA_LABELS[name] || name || '未知';
    },

    keyTs(key) {
      const match = String(key || '').match(/_(\d{13})\.jpg$/);
      return match ? Number(match[1]) : 0;
    },

    formatTime(ts) {
      return ts ? dayjs(ts).format('MM-DD HH:mm:ss') : '';
    },
  },
};
</script>

<style scoped>
.picker-title {
  display: flex;
  align-items: baseline;
  gap: 12px;
}
.picker-sub {
  font-size: 12px;
  font-weight: normal;
  color: #909399;
}
.picker-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.picker-actions {
  display: flex;
  gap: 6px;
}
.picker-summary {
  margin-bottom: 10px;
  font-size: 13px;
  color: #303133;
}
.picker-muted {
  margin-left: 10px;
  color: #909399;
}
.picker-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 12px;
  max-height: 56vh;
  overflow-y: auto;
  padding: 4px;
  min-height: 120px;
}
.pick-card {
  position: relative;
  border: 2px solid #409eff;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  background: #fff;
  transition: border-color 0.15s, opacity 0.15s;
}
.pick-card.off {
  border-color: #dcdfe6;
  border-style: dashed;
}
.pick-card.off .pick-img {
  opacity: 0.35;
  filter: grayscale(1);
}
.pick-img {
  display: block;
  width: 100%;
  height: 118px;
  object-fit: cover;
  background: #f5f7fa;
}
.pick-check {
  position: absolute;
  top: 6px;
  right: 8px;
  padding: 2px 4px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.85);
}
.pick-meta {
  display: flex;
  justify-content: space-between;
  gap: 6px;
  padding: 4px 6px;
  font-size: 12px;
  color: #606266;
}
.picker-empty {
  grid-column: 1 / -1;
  padding: 36px 0;
  text-align: center;
  color: #909399;
}
.pager {
  margin-top: 10px;
  text-align: right;
}
</style>
